/**
 * Which background effects are on for this viewer, on this page, right now.
 *
 * ONE gate for every consumer: the gradient, the starfield canvas and the
 * light-mode dot grid in ClientLayout, the add-to-cart shooting star and the
 * seller constellations.
 * They read it through useCosmicEffects (src/hooks/use-cosmic-effects.ts); the
 * decision itself lives here, pure, so the node test runner can pin it
 * (src/lib/cosmic-effects.test.ts). `stars` is THE flag for the seller
 * constellations: none twinkles unless the field itself is showing. The
 * add-to-cart star has a gate of its own (resolveCartFlight, at the end).
 *
 * The rules, in the order they bite:
 *
 *   1. Nothing before the component has mounted AND the viewer's preferences
 *      are known. Mounted, because the server cannot read the OS theme or
 *      motion setting, so the first client render has to match its markup.
 *      Prefs-ready, so a signed-in viewer's own settings are what paint, never
 *      a default that may not be theirs (every default is off, and older
 *      accounts hold the starfield and gradient on).
 *   2. Nothing at checkout, whatever anyone chose (owner decision 2026-09-29).
 *      The page takes card details; decoration behind it is a distraction.
 *   3. Nothing new while the page's seller override is still loading. A
 *      product page learns its design only after the product (and often the
 *      seller's brand, a second read) arrives, and a storefront after its user
 *      document does; until then `override` is null for "unknown", not for
 *      "none". Reading the viewer's toggles in that window painted
 *      the very effects a seller had switched off, then tore them down. So
 *      the gate HOLDS what the caller last showed (`held`), which is nothing on
 *      a first paint and the previous page's effects on a client-side
 *      navigation, where turning them off to wait would blink every product
 *      page that sets no design. Held stars and planets still obey the theme's
 *      own switches and the motion setting, which no override can lift. The
 *      dot grid is not held: only seller pages wait for a design, and none
 *      shows the grid, so it goes as soon as one starts loading.
 *   4. The gradient is dark-mode only (owner decision 2026-09-30: no gradient
 *      in light mode), and static, so it ignores the reduced-motion setting.
 *      A seller's override replaces the viewer's toggle on the seller's page.
 *      It is never drawn on the marketplace or search pages (owner decision
 *      2026-10-06), whatever the viewer chose; their stars and planets are
 *      untouched. A held gradient obeys this too, so arriving there from a
 *      page that had one never carries it in.
 *   5. Stars and planets move, so they need no reduced-motion request, under a
 *      seller override too: reduced motion is the one signal a seller cannot
 *      switch off for a visitor. The dot grid stays for it: still, it is a
 *      texture like the gradient, and ClientLayout leaves out the part of it
 *      that moves.
 *   6. Each mode has its own background (owner, 2026-10-23): the starfield is
 *      DARK mode's, on by default, and a seller's override decides it on
 *      their page; the dot grid is LIGHT mode's, on by default, and never on
 *      a seller's page, whose design has no grid. Neither shows in the other
 *      mode. Light mode once had a starfield of its own, which the grid
 *      replaced.
 *   7. Planets are always the viewer's own switch, one per mode: on by
 *      default in dark mode (owner, 2026-10-26), off in light mode. A seller
 *      cannot force them on (the design map has no planets
 *      key), but a seller who turned the starfield off on their page takes
 *      the planets with it, since the planets live in that field.
 *
 * A SELLER'S PAGE IS PLAIN UNLESS THE SELLER SAYS OTHERWISE (owner decision
 * 2026-10-19). Product pages and storefronts never hand this gate a null
 * override: a seller who set nothing publishes PLAIN_SELLER_PAGE, both effects
 * off (sellerPageOverride in src/lib/product-design.ts). So on a seller's page
 * the stars, planets and gradient show only where the seller switched them on,
 * still subject to rules 2 and 4 to 7, the grid never, and the viewer's own
 * toggles above decide every other page.
 */

export type CosmicTheme = 'dark' | 'light';

/** The two fields of a seller's DesignOverride this gate reads, or null for none. */
export type CosmicOverride = { starfield: boolean; accentBackground: boolean } | null | undefined;

export type CosmicViewerPrefs = {
  /** Dark-mode switches. */
  showStarfield: boolean;
  showGradient: boolean;
  showPlanets: boolean;
  /** Light-mode switches. */
  showPlanetsInLight: boolean;
  showDotGrid: boolean;
};

export type CosmicEffects = { stars: boolean; planets: boolean; gradient: boolean; grid: boolean };

export type CosmicInputs = {
  mounted: boolean;
  prefsReady: boolean;
  /** The open page has an override on the way and `override` does not reflect it yet. */
  overridePending: boolean;
  /** What this caller showed last with no override pending; held while one is. Absent reads as nothing. */
  held?: CosmicEffects;
  pathname: string | null | undefined;
  theme: CosmicTheme;
  reducedMotion: boolean;
  override: CosmicOverride;
  viewer: CosmicViewerPrefs;
};

export const NO_COSMIC_EFFECTS: CosmicEffects = Object.freeze({ stars: false, planets: false, gradient: false, grid: false });

/**
 * The checkout route family: /checkout and anything under it (/checkout/return).
 * Not a bare prefix match, which would also catch an unrelated '/checkoutx'.
 */
export function isCheckoutPath(pathname: string | null | undefined): boolean {
  if (typeof pathname !== 'string') return false;
  return pathname === '/checkout' || pathname.startsWith('/checkout/');
}

/** Route families that never draw the gradient, matched the way isCheckoutPath is. */
const GRADIENT_FREE_ROUTES = ['/marketplace', '/search'] as const;

/** The marketplace and search pages (and anything under them): no gradient there. */
export function isGradientFreePath(pathname: string | null | undefined): boolean {
  if (typeof pathname !== 'string') return false;
  return GRADIENT_FREE_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

export function resolveCosmicEffects(input: CosmicInputs): CosmicEffects {
  if (!input.mounted || !input.prefsReady) return NO_COSMIC_EFFECTS;
  if (isCheckoutPath(input.pathname)) return NO_COSMIC_EFFECTS;

  const { override, viewer } = input;
  const dark = input.theme === 'dark';
  const gradientAllowed = dark && !isGradientFreePath(input.pathname);
  const canMove = !input.reducedMotion;
  const planetsWanted = dark ? viewer.showPlanets : viewer.showPlanetsInLight;

  if (input.overridePending) {
    const held = input.held ?? NO_COSMIC_EFFECTS;
    return {
      stars: canMove && dark && held.stars,
      planets: canMove && planetsWanted && held.planets,
      gradient: gradientAllowed && held.gradient,
      // Only seller pages wait for a design, and none shows the grid: holding
      // it would keep it, and its ripple, on the seller's page a moment longer.
      grid: false,
    };
  }

  const gradient = gradientAllowed && (override ? override.accentBackground : viewer.showGradient);
  const stars = canMove && dark && (override ? override.starfield : viewer.showStarfield);
  const planets = canMove && planetsWanted && (override ? override.starfield : true);
  const grid = !dark && !override && viewer.showDotGrid;

  return { stars, planets, gradient, grid };
}

/**
 * The add-to-cart shooting star (src/lib/cart-flight.ts) is feedback for an
 * add, not part of the sky, so it plays whatever the starfield switch says,
 * in either theme and on a seller's page too (owner, 2026-10-22, when the
 * starfield went off by default and took the star with it). Never before
 * mount (the server cannot know the motion setting), at checkout, or against
 * a reduced-motion request.
 */
export function resolveCartFlight(input: Pick<CosmicInputs, 'mounted' | 'pathname' | 'reducedMotion'>): boolean {
  return input.mounted && !input.reducedMotion && !isCheckoutPath(input.pathname);
}
